import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiFetch } from '../api/client';
import { toast } from '../store/useToasts';
import type {
  GradebookCohort,
  GradebookMatrix,
  GradebookStudentDetail,
  GradingPolicy,
  GradingPolicyResponse,
  ReviewDecision,
  ReviewQueueItem,
} from '../api/types';

/**
 * Instructor layer hooks — backed by `services/gradebook/handler.py` (matrix,
 * drill-down, review queue, review) and `services/lab/handler.py` (grading
 * policy). Instructors see the cohorts they own; org admins see all.
 */

export const gradebookKeys = {
  all: ['gradebook'] as const,
  cohorts: () => [...gradebookKeys.all, 'cohorts'] as const,
  matrix: (id: string) => [...gradebookKeys.all, 'matrix', id] as const,
  student: (id: string, userId: string) => [...gradebookKeys.all, 'student', id, userId] as const,
  queue: () => [...gradebookKeys.all, 'queue'] as const,
  policy: (slug?: string) => [...gradebookKeys.all, 'policy', slug ?? 'tenant'] as const,
};

export function useGradebookCohorts() {
  return useQuery<ReadonlyArray<GradebookCohort>>({
    queryKey: gradebookKeys.cohorts(),
    queryFn: async ({ signal }) => {
      const r = await apiFetch<{ items?: ReadonlyArray<GradebookCohort> }>('/gradebook/cohorts', {
        method: 'GET',
        signal,
      });
      return r.items ?? [];
    },
    staleTime: 60_000,
  });
}

export function useCohortMatrix(id: string | undefined, assignedOnly = false) {
  return useQuery<GradebookMatrix>({
    queryKey: [...gradebookKeys.matrix(id ?? ''), assignedOnly ? 'assigned' : 'all'],
    enabled: Boolean(id),
    queryFn: async ({ signal }) =>
      apiFetch<GradebookMatrix>(assignedOnly ? '/gradebook/cohorts/{id}?assigned=1' : '/gradebook/cohorts/{id}', {
        method: 'GET',
        pathParams: { id: id ?? '' },
        signal,
      }),
    staleTime: 30_000,
  });
}

export function useStudentDetail(id: string | undefined, userId: string | undefined) {
  return useQuery<GradebookStudentDetail>({
    queryKey: gradebookKeys.student(id ?? '', userId ?? ''),
    enabled: Boolean(id && userId),
    queryFn: async ({ signal }) =>
      apiFetch<GradebookStudentDetail>('/gradebook/cohorts/{id}/students/{userId}', {
        method: 'GET',
        pathParams: { id: id ?? '', userId: userId ?? '' },
        signal,
      }),
    staleTime: 30_000,
  });
}

export function useReviewQueue() {
  return useQuery<ReadonlyArray<ReviewQueueItem>>({
    queryKey: gradebookKeys.queue(),
    queryFn: async ({ signal }) => {
      const r = await apiFetch<{ items?: ReadonlyArray<ReviewQueueItem> }>('/gradebook/review-queue', {
        method: 'GET',
        signal,
      });
      return r.items ?? [];
    },
    staleTime: 15_000,
  });
}

export function useReviewCompletion() {
  const qc = useQueryClient();
  return useMutation<unknown, Error, { completionId: number } & ReviewDecision>({
    mutationFn: async ({ completionId, ...decision }) =>
      apiFetch('/gradebook/completions/{id}/review', {
        method: 'POST',
        pathParams: { id: String(completionId) },
        body: decision,
      }),
    onSuccess: () => {
      toast.success('Review saved');
      qc.invalidateQueries({ queryKey: gradebookKeys.all });
    },
    onError: (e) => toast.error(e.message || 'Review failed'),
  });
}

/** The CSV route needs the auth header, so it cannot be a plain `<a href>`. */
export async function downloadCohortCsv(id: string, name: string): Promise<void> {
  const text = await apiFetch<string>('/gradebook/cohorts/{id}/export.csv', {
    method: 'GET',
    pathParams: { id },
    raw: true,
  });
  const blob = new Blob([text], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `gradebook-${name.replace(/[^\w-]+/g, '-')}.csv`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

// ---- grading policy (lab service) --------------------------------------

export function useGradingPolicy(slug?: string) {
  return useQuery<GradingPolicyResponse>({
    queryKey: gradebookKeys.policy(slug),
    queryFn: async ({ signal }) =>
      slug
        ? apiFetch<GradingPolicyResponse>('/labs/{slug}/policy', {
            method: 'GET',
            pathParams: { slug },
            signal,
          })
        : apiFetch<GradingPolicyResponse>('/grading/policy', { method: 'GET', signal }),
    staleTime: 60_000,
  });
}

export function useSetGradingPolicy(slug?: string) {
  const qc = useQueryClient();
  return useMutation<GradingPolicyResponse, Error, GradingPolicy>({
    mutationFn: async (policy) =>
      slug
        ? apiFetch<GradingPolicyResponse>('/labs/{slug}/policy', {
            method: 'PUT',
            pathParams: { slug },
            body: policy,
          })
        : apiFetch<GradingPolicyResponse>('/grading/policy', { method: 'PUT', body: policy }),
    onSuccess: () => {
      toast.success(slug ? 'Lab policy saved' : 'Default policy saved');
      qc.invalidateQueries({ queryKey: gradebookKeys.policy(slug) });
      qc.invalidateQueries({ queryKey: ['labs'] });
    },
    onError: (e) => toast.error(e.message || 'Could not save policy'),
  });
}

export function useClearLabPolicy(slug: string) {
  const qc = useQueryClient();
  return useMutation<unknown, Error, void>({
    mutationFn: async () =>
      apiFetch('/labs/{slug}/policy', { method: 'DELETE', pathParams: { slug } }),
    onSuccess: () => {
      toast.success('Lab override removed');
      qc.invalidateQueries({ queryKey: gradebookKeys.policy(slug) });
    },
  });
}

// ---- assignments, report, leaderboard (2026-09-18) --------------------

import type {
  CohortReport,
  LabAssignment,
  LeaderboardEntry,
  MyAssignment,
} from '../api/types';

export const assignmentKeys = {
  cohort: (id: string) => ['gradebook', 'assignments', id] as const,
  mine: () => ['gradebook', 'assignments', 'me'] as const,
  report: (id: string) => ['gradebook', 'report', id] as const,
  leaderboard: (id: string) => ['gradebook', 'leaderboard', id] as const,
};

export function useCohortAssignments(id: string | undefined) {
  return useQuery<ReadonlyArray<LabAssignment>>({
    queryKey: assignmentKeys.cohort(id ?? ''),
    enabled: Boolean(id),
    queryFn: async ({ signal }) => {
      const r = await apiFetch<{ items?: ReadonlyArray<LabAssignment> }>('/cohorts/{id}/assignments', {
        method: 'GET',
        pathParams: { id: id ?? '' },
        signal,
      });
      return r.items ?? [];
    },
    staleTime: 30_000,
  });
}

export function useAssignLabs(id: string) {
  const qc = useQueryClient();
  return useMutation<{ assigned: number }, Error, { lab_slugs: string[]; due_at?: string | null; required?: boolean }>({
    mutationFn: async (input) =>
      apiFetch('/cohorts/{id}/assignments', { method: 'POST', pathParams: { id }, body: input }),
    onSuccess: () => {
      toast.success('Labs assigned');
      qc.invalidateQueries({ queryKey: ['gradebook'] });
    },
    onError: (e) => toast.error(e.message || 'Could not assign'),
  });
}

export function useUnassignLab(id: string) {
  const qc = useQueryClient();
  return useMutation<unknown, Error, { labId: number }>({
    mutationFn: async ({ labId }) =>
      apiFetch('/cohorts/{id}/assignments/{labId}', { method: 'DELETE', pathParams: { id, labId: String(labId) } }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['gradebook'] }),
  });
}

export function useMyAssignments() {
  return useQuery<ReadonlyArray<MyAssignment>>({
    queryKey: assignmentKeys.mine(),
    queryFn: async ({ signal }) => {
      const r = await apiFetch<{ items?: ReadonlyArray<MyAssignment> }>('/me/assignments', { method: 'GET', signal });
      return r.items ?? [];
    },
    staleTime: 60_000,
  });
}

export function useCohortReport(id: string | undefined) {
  return useQuery<CohortReport>({
    queryKey: assignmentKeys.report(id ?? ''),
    enabled: Boolean(id),
    queryFn: async ({ signal }) =>
      apiFetch<CohortReport>('/gradebook/cohorts/{id}/report', { method: 'GET', pathParams: { id: id ?? '' }, signal }),
    staleTime: 30_000,
  });
}

export function useLeaderboard(id: string | undefined) {
  return useQuery<ReadonlyArray<LeaderboardEntry>>({
    queryKey: assignmentKeys.leaderboard(id ?? ''),
    enabled: Boolean(id),
    queryFn: async ({ signal }) => {
      const r = await apiFetch<{ items?: ReadonlyArray<LeaderboardEntry> }>('/gradebook/cohorts/{id}/leaderboard', {
        method: 'GET',
        pathParams: { id: id ?? '' },
        signal,
      });
      return r.items ?? [];
    },
    staleTime: 30_000,
  });
}

export async function downloadCohortReportCsv(id: string, name: string): Promise<void> {
  const text = await apiFetch<string>('/gradebook/cohorts/{id}/report.csv', {
    method: 'GET',
    pathParams: { id },
    raw: true,
  });
  const blob = new Blob([text], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `report-${name.replace(/[^\w-]+/g, '-')}.csv`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}
